import { useState, useEffect } from 'react';
import { adminApi } from './AdminContext';
import { 
  Users, Building2, TrendingUp, Clock, Plus, Search, 
  Menu, X, LogOut, Home, UserCheck, Truck, Camera,
  Phone, ChevronRight, Handshake
} from 'lucide-react';

const AdminDashboard = ({ onNavigate, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await adminApi.getDashboard();
      setStats(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
    <div 
      className={`bg-[#0A0F1C] border border-white/10 rounded-lg p-4 ${onClick ? 'cursor-pointer hover:border-white/20' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm">{label}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </div>
  );

  const QuickAction = ({ icon: Icon, label, onClick, color }) => (
    <button
      onClick={onClick}
      className="flex items-center gap-3 w-full p-4 bg-[#0A0F1C] border border-white/10 rounded-lg hover:border-white/20 transition-colors"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <span className="text-white font-medium">{label}</span>
      <ChevronRight size={20} className="text-slate-400 ml-auto" />
    </button>
  );

  const RecentEntry = ({ item, type }) => (
    <div 
      className="flex items-center justify-between p-3 bg-[#0A0F1C]/50 rounded-lg cursor-pointer hover:bg-[#0A0F1C]"
      onClick={() => onNavigate(type === 'laborer' ? 'laborers' : type === 'supplier' ? 'suppliers' : 'partners')}
    >
      <div>
        <p className="text-white font-medium">
          {type === 'laborer' ? item.name : type === 'supplier' ? item.company_name : item.company_name}
        </p>
        <p className="text-slate-400 text-sm">
          {type === 'laborer' ? item.labor_type : type === 'supplier' ? item.supplier_type : item.partnership_type}
        </p>
      </div>
      {item.phone && (
        <a 
          href={`https://wa.me/${item.phone.replace(/[^0-9]/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-8 h-8 bg-[#25D366] rounded-full flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Phone size={14} className="text-white" />
        </a>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060B14] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#25D366] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060B14]">
      {/* Header */}
      <header className="bg-[#0A0F1C] border-b border-white/10 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden text-white p-2"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Glow Space Admin
            </h1>
          </div>
          <button 
            onClick={onLogout}
            className="text-slate-400 hover:text-white p-2"
          >
            <LogOut size={20} />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#0A0F1C] p-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white font-bold">Menu</h2>
              <button onClick={() => setMobileMenuOpen(false)} className="text-white">
                <X size={24} />
              </button>
            </div>
            <nav className="space-y-2">
              <button 
                onClick={() => { onNavigate('dashboard'); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-3 text-white bg-[#25D366]/20 rounded-lg"
              >
                <Home size={20} /> Dashboard
              </button>
              <button 
                onClick={() => { onNavigate('laborers'); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg"
              >
                <UserCheck size={20} /> Laborers
              </button>
              <button 
                onClick={() => { onNavigate('suppliers'); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg"
              >
                <Truck size={20} /> Suppliers
              </button>
              <button 
                onClick={() => { onNavigate('partners'); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg"
              >
                <Handshake size={20} /> Partners
              </button>
              <button 
                onClick={() => { onNavigate('scanner'); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full p-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg"
              >
                <Camera size={20} /> Card Scanner
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="p-4 pb-24">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <StatCard 
            icon={Users} 
            label="Laborers" 
            value={stats?.total_laborers || 0}
            color="bg-blue-500"
            onClick={() => onNavigate('laborers')}
          />
          <StatCard 
            icon={Building2} 
            label="Suppliers" 
            value={stats?.total_suppliers || 0}
            color="bg-purple-500"
            onClick={() => onNavigate('suppliers')}
          />
          <StatCard 
            icon={Handshake} 
            label="Partners" 
            value={stats?.total_partners || 0}
            color="bg-cyan-500"
            onClick={() => onNavigate('partners')}
          />
          <StatCard 
            icon={Clock} 
            label="Available" 
            value={stats?.availability?.available || 0}
            color="bg-orange-500"
          />
        </div>

        {/* Quick Actions */}
        <h2 className="text-white font-semibold mb-3">Quick Actions</h2>
        <div className="space-y-2 mb-6">
          <QuickAction 
            icon={Plus} 
            label="Add New Laborer" 
            onClick={() => onNavigate('laborers', { action: 'add' })}
            color="bg-blue-500"
          />
          <QuickAction 
            icon={Plus} 
            label="Add New Supplier" 
            onClick={() => onNavigate('suppliers', { action: 'add' })}
            color="bg-purple-500"
          />
          <QuickAction 
            icon={Plus} 
            label="Add New Partner" 
            onClick={() => onNavigate('partners', { action: 'add' })}
            color="bg-cyan-500"
          />
          <QuickAction 
            icon={Camera} 
            label="Scan Business Card" 
            onClick={() => onNavigate('scanner')}
            color="bg-[#25D366]"
          />
        </div>

        {/* Recent Laborers */}
        {stats?.recent_laborers?.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-semibold">Recent Laborers</h2>
              <button 
                onClick={() => onNavigate('laborers')}
                className="text-[#25D366] text-sm"
              >
                View All
              </button>
            </div>
            <div className="space-y-2">
              {stats.recent_laborers.slice(0, 3).map((laborer) => (
                <RecentEntry key={laborer.id} item={laborer} type="laborer" />
              ))}
            </div>
          </div>
        )}

        {/* Recent Suppliers */}
        {stats?.recent_suppliers?.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-semibold">Recent Suppliers</h2>
              <button 
                onClick={() => onNavigate('suppliers')}
                className="text-[#25D366] text-sm"
              >
                View All
              </button>
            </div>
            <div className="space-y-2">
              {stats.recent_suppliers.slice(0, 3).map((supplier) => (
                <RecentEntry key={supplier.id} item={supplier} type="supplier" />
              ))}
            </div>
          </div>
        )}

        {/* Recent Partners */}
        {stats?.recent_partners?.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-semibold">Recent Partners</h2>
              <button 
                onClick={() => onNavigate('partners')}
                className="text-[#25D366] text-sm"
              >
                View All
              </button>
            </div>
            <div className="space-y-2">
              {stats.recent_partners.slice(0, 3).map((partner) => (
                <RecentEntry key={partner.id} item={partner} type="partner" />
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#0A0F1C] border-t border-white/10 px-2 py-2 flex justify-around">
        <button 
          onClick={() => onNavigate('dashboard')}
          className="flex flex-col items-center gap-1 text-[#25D366]"
        >
          <Home size={18} />
          <span className="text-[10px]">Home</span>
        </button>
        <button 
          onClick={() => onNavigate('laborers')}
          className="flex flex-col items-center gap-1 text-slate-400"
        >
          <UserCheck size={18} />
          <span className="text-[10px]">Laborers</span>
        </button>
        <button 
          onClick={() => onNavigate('suppliers')}
          className="flex flex-col items-center gap-1 text-slate-400"
        >
          <Truck size={18} />
          <span className="text-[10px]">Suppliers</span>
        </button>
        <button 
          onClick={() => onNavigate('partners')}
          className="flex flex-col items-center gap-1 text-slate-400"
        >
          <Handshake size={18} />
          <span className="text-[10px]">Partners</span>
        </button>
        <button 
          onClick={() => onNavigate('scanner')}
          className="flex flex-col items-center gap-1 text-slate-400"
        >
          <Camera size={18} />
          <span className="text-[10px]">Scan</span>
        </button>
      </nav>
    </div>
  );
};

export default AdminDashboard;
